import i18n from '@/i18n/config'
import { languagesApi } from '@/services/languagesApiService'
import { updateLanguageDirections, applyDirection } from '@/i18n/config'
import {
  getLangFromPathname,
  SUPPORTED_LANG_CODES,
  DEFAULT_LANG,
  getClientUiLanguages,
  directionForLangCode,
  type LangCode,
} from '@/utils/localeFromUrl'

function normalizeLangCode(code: string): string {
  return code.split('-')[0].toLowerCase()
}

/**
 * Initialize language from backend enabled languages + URL/localStorage.
 * Only languages both enabled in API and present in SUPPORTED_LANG_CODES get full UI (routes + i18n).
 */
export async function initializeLanguages(): Promise<void> {
  try {
    const enabledLanguages = await languagesApi.getEnabledLanguages()
    const supportedEnabled = enabledLanguages.filter((l) =>
      SUPPORTED_LANG_CODES.includes(normalizeLangCode(l.code) as LangCode),
    )

    if (supportedEnabled.length === 0) {
      console.warn('No enabled languages from API (or none match site bundles); using client fallback')
      await applyClientOnlyInit()
      return
    }

    updateLanguageDirections(
      enabledLanguages.map((l) => ({
        code: normalizeLangCode(l.code),
        direction: l.direction === 'rtl' ? 'rtl' : 'ltr',
      })),
    )

    const defaultRow = supportedEnabled.find((l) => l.isDefault) || supportedEnabled[0]
    const defaultCode = normalizeLangCode(defaultRow.code) as LangCode

    const storedBase = localStorage.getItem('i18nextLng')?.split('-')[0]?.toLowerCase()
    const urlLang =
      typeof window !== 'undefined' ? getLangFromPathname(window.location.pathname) : null

    const isAllowed = (code: string | undefined): code is LangCode =>
      !!code &&
      supportedEnabled.some((l) => normalizeLangCode(l.code) === code.toLowerCase())

    let languageToUse: LangCode = defaultCode
    if (urlLang && isAllowed(urlLang)) {
      languageToUse = urlLang
    } else if (storedBase && isAllowed(storedBase)) {
      languageToUse = storedBase as LangCode
    }

    const currentBase = i18n.language?.split('-')[0] || ''
    if (currentBase.toLowerCase() !== languageToUse) {
      await i18n.changeLanguage(languageToUse)
      try {
        localStorage.setItem('i18nextLng', languageToUse)
      } catch {
        /* ignore */
      }
    }

    const row = supportedEnabled.find((l) => normalizeLangCode(l.code) === languageToUse)
    applyDirection(languageToUse, row?.direction === 'rtl' ? 'rtl' : 'ltr')

    if (i18n.options.supportedLngs) {
      const codes = new Set<string>([...SUPPORTED_LANG_CODES])
      enabledLanguages.forEach((l) => codes.add(normalizeLangCode(l.code)))
      i18n.options.supportedLngs = [...codes]
    }
  } catch (error) {
    console.error('Failed to initialize languages from API:', error)
    await applyClientOnlyInit()
  }
}

async function applyClientOnlyInit(): Promise<void> {
  const clientList = getClientUiLanguages()
  updateLanguageDirections(clientList.map((l) => ({ code: l.code, direction: l.direction })))

  const languageToUse = DEFAULT_LANG
  await i18n.changeLanguage(languageToUse)
  try {
    localStorage.setItem('i18nextLng', languageToUse)
  } catch {
    /* ignore */
  }
  applyDirection(languageToUse, directionForLangCode(languageToUse))

  if (i18n.options.supportedLngs) {
    i18n.options.supportedLngs = [...SUPPORTED_LANG_CODES]
  }
}
